/**
 * BulkDraftDrawResetModal
 *
 * Confirmation modal for the rodeo-level "Reset All Draft Draws" and
 * "Reset & Run Initial Draw for Drafts" header actions. Lists exactly
 * which events will be affected (currently in DRAFT) and which will be
 * skipped (already published / finalized, or no draw run yet) so the
 * admin can see the blast radius before they commit.
 *
 * Both actions operate ONLY on DRAFT events server-side — published
 * and finalized rows are never touched. The skipped lists are shown
 * here so the admin understands why a given event isn't in the
 * affected set rather than wondering whether it was missed.
 *
 * `result` is the per-event outcome report returned by the bulk action.
 * When present it replaces the preview table with a results table so the
 * admin can see exactly what happened (success / failed / skipped) for
 * each event — partial failures are normal here, not a rollback case.
 */

import { AlertTriangle, CheckCircle2, Loader2, XCircle, Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type {
  BulkDraftActionReport,
  BulkDraftPreviewReport,
} from '@/api/extensions/useDrawOps';

export type BulkDraftMode = 'reset' | 'run-initial';

interface BulkDraftDrawResetModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  mode: BulkDraftMode;
  preview: BulkDraftPreviewReport | undefined;
  isLoadingPreview: boolean;
  isRunning: boolean;
  /** When set, the modal shows per-event outcomes instead of the preview. */
  result: BulkDraftActionReport | null;
}

const COPY: Record<BulkDraftMode, { title: string; verb: string; desc: string }> = {
  reset: {
    title: 'Reset all draft draws',
    verb: 'Reset draft draws',
    desc:
      'This will wipe every DRAFT draw in the rodeo back to "no draw entries". ' +
      'Events that have never been drawn, or whose draw is already locked or ' +
      'finalized, are not affected.',
  },
  'run-initial': {
    title: 'Run initial draw for un-drawn events',
    verb: 'Run initial draw',
    desc:
      'This will run the initial placement draw for every event that has NOT ' +
      'been drawn yet (status: not started). Events already in DRAFT are ' +
      'intentionally skipped — if you want to re-run those, click "Reset All ' +
      'Draft Draws" first and then come back here. Locked and finalized ' +
      'draws are never touched.',
  },
};

export function BulkDraftDrawResetModal({
  open,
  onClose,
  onConfirm,
  mode,
  preview,
  isLoadingPreview,
  isRunning,
  result,
}: BulkDraftDrawResetModalProps) {
  const affected = preview?.affected_events ?? [];
  const skippedPubFinal = preview?.skipped_published_or_finalized ?? [];
  const skippedNoExec = preview?.skipped_no_execution ?? [];
  const copy = COPY[mode];
  const showingResults = !!result;

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-5 w-5" />
            {showingResults
              ? `${copy.verb} — results`
              : `${copy.title}?`}
          </DialogTitle>
          {!showingResults && (
            <DialogDescription className="pt-2 leading-relaxed">
              {copy.desc}{' '}
              <strong>This action cannot be reversed.</strong>
            </DialogDescription>
          )}
          {showingResults && result && (
            <DialogDescription className="pt-2 leading-relaxed">
              Finished — {result.successful} succeeded
              {result.failed > 0 ? `, ${result.failed} failed` : ''}
              {result.skipped > 0 ? `, ${result.skipped} skipped` : ''}.
            </DialogDescription>
          )}
        </DialogHeader>

        {/* PREVIEW STATE */}
        {!showingResults && (
          <>
            {isLoadingPreview ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading affected events…
              </div>
            ) : affected.length === 0 ? (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100">
                {mode === 'reset'
                  ? 'No events are currently in draft — nothing to reset.'
                  : 'No un-drawn events found — every event in this rodeo either has a draft draw already or has been locked/finalized.'}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="rounded-md border bg-card px-4 py-3">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Events affected
                  </div>
                  <div className="text-2xl font-semibold tabular-nums">
                    {affected.length}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {mode === 'reset'
                      ? 'currently in draft — will be reset to no draw entries'
                      : 'currently not started — initial draw will run'}
                  </div>
                </div>

                <div className="max-h-60 overflow-y-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 font-medium">Event</th>
                        <th className="px-3 py-2 text-right font-medium">
                          Entries
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                          Placed
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {affected.map((ev) => (
                        <tr
                          key={ev.event_id}
                          className="border-t border-border last:border-b-0"
                        >
                          <td className="px-3 py-2 font-medium">
                            {ev.event_name}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {ev.entry_count}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {ev.placed_count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {(skippedPubFinal.length > 0 || skippedNoExec.length > 0) && (
              <div className="rounded-md border border-border bg-muted/30 px-3 py-2 space-y-1.5">
                <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <Info className="h-3.5 w-3.5" />
                  Not affected
                </div>
                {skippedPubFinal.length > 0 && (
                  <div className="text-xs text-muted-foreground leading-relaxed">
                    {skippedPubFinal.length} event
                    {skippedPubFinal.length === 1 ? '' : 's'} skipped because
                    the draw is already locked or finalized:{' '}
                    <span className="font-medium text-foreground/80">
                      {skippedPubFinal
                        .map(
                          (s) =>
                            `${s.event_name} (${s.placement_status})`,
                        )
                        .join(', ')}
                    </span>
                  </div>
                )}
                {skippedNoExec.length > 0 && (
                  <div className="text-xs text-muted-foreground leading-relaxed">
                    {skippedNoExec.length} event
                    {skippedNoExec.length === 1 ? '' : 's'} skipped —{' '}
                    {mode === 'reset'
                      ? 'no draw has been run yet'
                      : 'a draft draw already exists (reset it first if you want to re-run)'}
                    :{' '}
                    <span className="font-medium text-foreground/80">
                      {skippedNoExec.map((s) => s.event_name).join(', ')}
                    </span>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* RESULT STATE */}
        {showingResults && result && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <ResultStat
                label="Succeeded"
                value={result.successful}
                tone="success"
              />
              <ResultStat
                label="Failed"
                value={result.failed}
                tone={result.failed > 0 ? 'error' : 'muted'}
              />
              <ResultStat
                label="Skipped"
                value={result.skipped}
                tone="muted"
              />
            </div>

            <div className="max-h-72 overflow-y-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Event</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {result.results.map((r) => (
                    <tr
                      key={r.event_id}
                      className="border-t border-border last:border-b-0 align-top"
                    >
                      <td className="px-3 py-2 font-medium">
                        {r.event_name}
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground leading-relaxed">
                        {r.status === 'success' &&
                          mode === 'run-initial' &&
                          typeof r.new_placed_count === 'number' && (
                            <span>
                              {r.new_placed_count} placed
                              {typeof r.new_entry_count === 'number' &&
                                ` of ${r.new_entry_count}`}
                              {r.warnings && r.warnings.length > 0 && (
                                <span className="block text-amber-600 dark:text-amber-400">
                                  {r.warnings.length} warning
                                  {r.warnings.length === 1 ? '' : 's'}
                                </span>
                              )}
                            </span>
                          )}
                        {r.status === 'success' &&
                          mode === 'reset' && <span>Draft cleared</span>}
                        {r.status !== 'success' && r.error && (
                          <span className="break-words">{r.error}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {showingResults ? (
            <Button onClick={onClose}>Close</Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isRunning}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={onConfirm}
                disabled={
                  isRunning || isLoadingPreview || affected.length === 0
                }
              >
                {isRunning ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    {mode === 'reset' ? 'Resetting…' : 'Running…'}
                  </>
                ) : (
                  <>
                    {copy.verb} ({affected.length})
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResultStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'success' | 'error' | 'muted';
}) {
  const toneClasses =
    tone === 'success'
      ? 'border-emerald-200 dark:border-emerald-900/60 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-900 dark:text-emerald-100'
      : tone === 'error'
        ? 'border-rose-200 dark:border-rose-900/60 bg-rose-50 dark:bg-rose-950/30 text-rose-900 dark:text-rose-100'
        : 'border-border bg-card text-foreground';
  return (
    <div className={`rounded-md border px-3 py-2 ${toneClasses}`}>
      <div className="text-[11px] uppercase tracking-wide opacity-80">
        {label}
      </div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: 'success' | 'failed' | 'skipped' }) {
  if (status === 'success') {
    return (
      <Badge className="bg-emerald-100 text-emerald-900 hover:bg-emerald-100 dark:bg-emerald-950/50 dark:text-emerald-100">
        <CheckCircle2 className="h-3 w-3 mr-1" /> Success
      </Badge>
    );
  }
  if (status === 'failed') {
    return (
      <Badge className="bg-rose-100 text-rose-900 hover:bg-rose-100 dark:bg-rose-950/50 dark:text-rose-100">
        <XCircle className="h-3 w-3 mr-1" /> Failed
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-muted-foreground">
      Skipped
    </Badge>
  );
}
